<script setup lang="ts">
import { changePassword } from '@yc/api-client'
import { TraceIdAlert } from '@yc/ui-kit'
import { computed, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const props = withDefaults(
  defineProps<{
    /** 改密成功后是否自动跳转（强制改密场景由外层决定） */
    redirectAfter?: boolean
  }>(),
  { redirectAfter: true },
)

const emit = defineEmits<{ (e: 'done'): void }>()

const route = useRoute()
const router = useRouter()

const form = reactive({ old_password: '', new_password: '', confirm: '' })
const submitting = ref(false)
const error = ref<unknown>(null)

const mismatch = computed(() => !!form.confirm && form.confirm !== form.new_password)

const canSubmit = computed(
  () => !!form.old_password && !!form.new_password && !!form.confirm && !mismatch.value && !submitting.value,
)

function redirectOf(): string {
  const q = route.query.redirect
  return typeof q === 'string' && q.startsWith('/') && !q.startsWith('//') ? q : '/'
}

async function submit(): Promise<void> {
  if (!canSubmit.value) return
  submitting.value = true
  error.value = null
  try {
    await changePassword({ old_password: form.old_password, new_password: form.new_password })
    emit('done')
    if (props.redirectAfter) await router.replace(redirectOf())
  } catch (e) {
    error.value = e
  } finally {
    submitting.value = false
  }
}

defineExpose({ form })
</script>

<template>
  <el-form label-position="top" @submit.prevent="submit">
    <el-form-item label="原密码">
      <el-input
        v-model="form.old_password"
        type="password"
        show-password
        autocomplete="current-password"
        name="old_password"
      />
    </el-form-item>
    <el-form-item label="新密码">
      <el-input
        v-model="form.new_password"
        type="password"
        show-password
        autocomplete="new-password"
        name="new_password"
      />
    </el-form-item>
    <el-form-item label="确认新密码" :error="mismatch ? '两次输入的新密码不一致' : ''">
      <el-input
        v-model="form.confirm"
        type="password"
        show-password
        autocomplete="new-password"
        name="confirm_password"
      />
    </el-form-item>

    <div class="yc-pwd-error">
      <TraceIdAlert v-if="error" :error="error" />
    </div>

    <el-button type="primary" native-type="submit" :loading="submitting" :disabled="!canSubmit">
      确认修改
    </el-button>
  </el-form>
</template>

<style scoped lang="scss">
.yc-pwd-error {
  min-height: 24px;
  margin-bottom: var(--yc-space-2);
  font-size: var(--yc-font-sm);
}
</style>